import { Transaction, TransactionFilter } from './merchant-transaction.model';
import { Observable } from 'rxjs';

/**
 * Generic service adapter interface that bridges entity-specific services
 * to the generic TransactionHistoryService interface.
 *
 * This interface allows different entity types (merchants, users, accounts, etc.)
 * to provide their own transaction service implementations while maintaining
 * a consistent interface for the shared transaction history component.
 *
 * @template TEntity The type of entity (e.g., Merchant, User, Account)
 * @template TTransaction The type of transaction (must extend Transaction)
 *
 * @example
 * ```typescript
 * // Implementation for merchant transactions
 * class MerchantTransactionServiceAdapter implements EntityTransactionServiceAdapter<Merchant, MerchantTransaction> {
 *   constructor(private merchantService: MerchantService) {}
 *
 *   getTransactions(merchantId: string, filter: TransactionFilter): Observable<{
 *     items: MerchantTransaction[];
 *     totalCount: number;
 *   }> {
 *     return this.merchantService.getMerchantTransactions(merchantId, filter);
 *   }
 *
 *   openSidebar(): void {
 *     this.merchantService.openSidebar();
 *   }
 *
 *   closeSidebar(): void {
 *     this.merchantService.closeSidebar();
 *   }
 * }
 * ```
 */
export interface EntityTransactionServiceAdapter<TEntity = any, TTransaction extends Transaction = Transaction> {
  /**
   * Get transactions for the specified entity
   * @param entityId The ID of the entity to get transactions for
   * @param filter The filter criteria for transactions
   * @returns Observable with paginated transaction results
   */
  getTransactions(
    entityId: string,
    filter: TransactionFilter,
  ): Observable<{
    items: TTransaction[];
    totalCount: number;
  }>;

  /**
   * Optional method to open sidebar for transaction details
   */
  openSidebar?(): void;

  /**
   * Optional method to close sidebar for transaction details
   */
  closeSidebar?(): void;
}

/**
 * Base class that provides common functionality for entity transaction service adapters.
 * Extend this class to create specific adapters for different entity types.
 *
 * @template TEntity The type of entity
 * @template TTransaction The type of transaction
 * @template TFilter The type of entity-specific filter (should extend or be compatible with TransactionFilter)
 */
export abstract class BaseEntityTransactionServiceAdapter<
  TEntity = any,
  TTransaction extends Transaction = Transaction,
  TFilter = any,
> implements EntityTransactionServiceAdapter<TEntity, TTransaction>
{
  /**
   * Abstract method to get transactions - must be implemented by subclasses
   */
  abstract getTransactions(
    entityId: string,
    filter: TransactionFilter,
  ): Observable<{
    items: TTransaction[];
    totalCount: number;
  }>;

  /**
   * Convert generic TransactionFilter to entity-specific filter format
   * Override this method in subclasses to handle entity-specific filter transformations
   * @param filter The generic transaction filter
   * @returns The entity-specific filter
   */
  protected transformFilter(filter: TransactionFilter): TFilter {
    // Default implementation - just return the filter as-is
    // Subclasses should override this to perform proper transformation
    return filter as unknown as TFilter;
  }

  /**
   * Default implementation for opening sidebar - can be overridden
   */
  openSidebar?(): void {
    // Default implementation - no-op
    // Subclasses can override to provide specific behavior
  }

  /**
   * Default implementation for closing sidebar - can be overridden
   */
  closeSidebar?(): void {
    // Default implementation - no-op
    // Subclasses can override to provide specific behavior
  }
}

/**
 * Factory function to create a simple service adapter from a service instance
 * This is useful for quick implementations where you don't need complex transformation logic
 *
 * @param service The entity service instance
 * @param getTransactionsMethod The method name on the service to get transactions
 * @param openSidebarMethod Optional method name to open sidebar
 * @param closeSidebarMethod Optional method name to close sidebar
 * @returns A service adapter instance
 */
export function createEntityTransactionServiceAdapter<TEntity, TTransaction extends Transaction>(
  service: any,
  getTransactionsMethod: string,
  openSidebarMethod?: string,
  closeSidebarMethod?: string,
): EntityTransactionServiceAdapter<TEntity, TTransaction> {
  return {
    getTransactions: (entityId: string, filter: TransactionFilter) => service[getTransactionsMethod](entityId, filter),
    openSidebar: openSidebarMethod ? () => service[openSidebarMethod]() : undefined,
    closeSidebar: closeSidebarMethod ? () => service[closeSidebarMethod]() : undefined,
  };
}
